/**
 * 会社概要ページ（/about）の掲載内容。
 * 出典: SOREMO CORPORATE PROFILE 2024（会社情報・取引先）、
 *       SOREMO GROWTH STRATEGY 2026 v2 — Asian Audio Partner（CONCEPT / FEATURE のコピー）。
 * 文言を変える場合はここだけ直す。
 */
import thumbYoshisato from "../../assets/images/about/about_yoshisato.webp";
import thumbTheSoho from "../../assets/images/about/about_thesoho.webp";
import thumbAoiumito from "../../assets/images/about/about_aoiumito.webp";
import thumbBrothership from "../../assets/images/about/about_brothership.webp";
import thumbDegico from "../../assets/images/about/about_degico.webp";

// ───────────────────────────── Hero ─────────────────────────────
/** ヒーローの英語大見出し（din-2014） */
export const ABOUT_HERO_TITLE = "Design with Sound";
/** ヒーローのサブタイトル（コーポレートコピー）。document.title にも使う */
export const ABOUT_HERO_SUBTITLE = "音でワクワクをデザインする";

// ─────────────────────── CONCEPT / FEATURE ──────────────────────
export interface AboutFeatureItem {
  /** "01" などの番号 */
  index: string;
  /** 英語の項目名（din-2014 で表示） */
  title: string;
  /** 項目の一言（任意） */
  lead?: string;
  body: string;
}

export interface AboutSectionCopy {
  /** セクション種別ラベル（CONCEPT / FEATURE） */
  label: string;
  /** 英語の大見出し */
  titleEn: string;
  /** 日本語の見出し（"\n" で改行位置を指定できる） */
  titleJa: string;
  /** 段落。"\n" は PC では改行、モバイルでは無視して自然に折り返す */
  paragraphs: string[];
  /** 番号付きの項目（FEATURE の 3 エンジン。任意） */
  items?: AboutFeatureItem[];
}

export const ABOUT_CONCEPT: AboutSectionCopy = {
  label: "CONCEPT",
  titleEn: "AS AN AUDIO PARTNER",
  titleJa: "音をつくる制作会社から、\n作品の音をまるごと任せられる存在へ。",
  paragraphs: [
    "コンポーザー、サウンドデザイナー、ボイスアクターをはじめ、さまざまな音のスペシャリストと連携。\n音の演出設計から制作、ゲームへの実装・調整まで、作品に必要なオーディオをひとつのチームで支えます。",
    "私たちが目指すのは、音を納品するだけの制作会社ではありません。\n作品を深く理解し、必要な専門性をつなぎ、そこでしか味わえない音をつくる。\nその音を通じて、作品の可能性と価値を広げていく。",
    "長く信頼して音を任せられる、Audio Partner でありたいと考えています。",
  ],
};

export const ABOUT_FEATURE: AboutSectionCopy = {
  label: "FEATURE",
  titleEn: "OUR MODEL",
  titleJa: "人と専門性をつなぎ、\nひとつのチームでつくる。",
  paragraphs: [
    "SOREMO OS を中心に、作品を託す人、制作を担う人、必要な専門性を持つ人がひとつのプロジェクトにつながります。\n情報や判断を共有しながら、それぞれが自分の専門に集中できるチームをつくります。",
  ],
  items: [
    {
      index: "01",
      title: "PRODUCTION",
      lead: "作品と、音をつくる人をつなぐ。",
      body: "演出設計から制作、実装・調整まで。作品を深く理解し、同じゴールを見ながら音づくりを進めます。",
    },
    {
      index: "02",
      title: "NETWORK",
      lead: "作品に必要な専門性をつなぐ。",
      body: "必要なときに、必要なスペシャリストが参加する。それぞれの得意を活かし、作品に合ったチームをつくります。",
    },
    {
      index: "03",
      title: "SOREMO OS",
      lead: "すべてを、ひとつの制作体験につなぐ。",
      body: "オファーから制作、レビュー、納品、支払いまで。人・情報・判断をつなぎ、関わる人それぞれが専門性を発揮できる環境を支えます。",
    },
  ],
};

// ─────────────────────────── PROFILE ────────────────────────────
export interface ProfileThumbnail {
  src: string;
  alt: string;
  /** 表示幅（px）。高さは比率に従う */
  width: number;
  /** 付けるとサムネイルがリンクになる */
  href?: string;
}

export interface ProfileLine {
  /** 表示テキスト。ロゴだけを見せる行は空文字にして thumbnail のみ渡す */
  text: string;
  /** 付けるとテキストがリンクになる */
  href?: string;
  /** 英語表記など、補助的に小さく添える行 */
  sub?: string;
  /** テキストの下に添えるサムネイル（代表者アイコン・ロゴ等） */
  thumbnail?: ProfileThumbnail;
  /** 控えめに表示する（主要取引先の羅列など） */
  muted?: boolean;
}

export interface ProfileRow {
  label: string;
  lines: ProfileLine[];
}

/** 主要取引先（順不同・敬称略）。CORPORATE PROFILE 2024 の掲載順。 */
/** 主要取引先（PROFILE の 1 行として 1 社ずつ掲載） */
const ABOUT_CLIENTS: string[] = [
  "アークシステムワークス株式会社",
  "株式会社i-cue",
  "イートレックジャパン株式会社",
  "イグニッションポイント株式会社",
  "イッツ・コミュニケーションズ株式会社",
  "株式会社エース・ワン",
  "LT Game Japan株式会社",
  "株式会社エルフィン",
  "株式会社オリバナ",
  "オンテック株式会社",
  "株式会社カプコン",
  "京楽ピクチャーズ. 株式会社",
  "株式会社GRASH",
  "株式会社ココー",
  "株式会社コナミデジタルエンタテインメント",
  "株式会社ザイクス",
  "サミー株式会社",
  "株式会社SANKYO",
  "サンセイアールアンドディ株式会社",
  "サン電子株式会社",
  "株式会社SCENE",
  "有限会社スタージャムコーポレーション",
  "有限会社スタジオアールエフ",
  "株式会社大一商会",
  "第２制作株式会社",
  "株式会社高尾",
  "株式会社ディー・オー",
  "株式会社虎猫企画",
  "株式会社ニューギン",
  "パナソニック株式会社",
  "Babeltime Inc.",
  "バロックシステムズ株式会社",
  "P-kraft",
  "株式会社BOOOM",
  "株式会社藤商事",
  "株式会社放送出版エージェンシー",
  "マルホン工業株式会社",
  "魔法株式会社",
  "株式会社ユニバーサルエンターテインメント",
  "WAGOON株式会社",
];

export const ABOUT_PROFILE: ProfileRow[] = [
  {
    label: "社名",
    lines: [{ text: "株式会社ソレモ", sub: "SOREMO Co.,Ltd." }],
  },
  {
    label: "設立",
    lines: [{ text: "2008年12月10日" }],
  },
  {
    label: "本店所在地",
    lines: [
      {
        text: "東京都江東区青海二丁目7番4号",
        sub: "theSOHO 1226, 2-7-4 Aomi, Koto-ku, Tokyo",
        thumbnail: {
          src: thumbTheSoho,
          alt: "theSOHO（アクセス）",
          width: 240,
          href: "https://rjsoho.com/#access",
        },
      },
    ],
  },
  {
    label: "代表取締役",
    lines: [
      {
        text: "善里 信哉",
        sub: "Shinya Yoshisato",
        thumbnail: {
          src: thumbYoshisato,
          alt: "善里 信哉",
          width: 96,
          href: "/s_yoshisato",
        },
      },
    ],
  },
  {
    label: "資本金",
    lines: [{ text: "1,000万円" }],
  },
  {
    label: "事業内容",
    lines: [
      { text: "サウンドコンテンツの企画制作" },
      { text: "上記に伴う開発支援システムの運営" },
    ],
  },
  {
    label: "取引先金融機関",
    lines: [
      { text: "楽天銀行" },
      { text: "みずほ銀行" },
      { text: "三井住友銀行" },
      { text: "西武信用金庫" },
    ],
  },
  {
    label: "関連施設",
    lines: [
      { text: "株式会社ソレモミュージックパブリッシング" },
      {
        text: "",
        thumbnail: {
          src: thumbAoiumito,
          alt: "DAW Studio アオイウミト",
          width: 150,
          href: "https://aoiumito.jp",
        },
      },
    ],
  },
  {
    label: "顧問税理士",
    lines: [
      {
        text: "",
        thumbnail: {
          src: thumbBrothership,
          alt: "税理士法人ブラザシップコンサルティング",
          width: 125,
          href: "https://www.brothership.co.jp",
        },
      },
    ],
  },
  {
    label: "顧問デザイナー",
    lines: [
      {
        text: "",
        thumbnail: {
          src: thumbDegico,
          alt: "DEGICO",
          width: 120,
          href: "https://www.degico.jp/",
        },
      },
    ],
  },
  {
    label: "主要取引先",
    lines: [
      ...ABOUT_CLIENTS.map((name) => ({ text: name })),
      { text: "（順不同・敬称略）", muted: true },
    ],
  },
];
